import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useRowMutation } from "@/lib/use-api-mutation";
import { workflowText } from "@/lib/workflow-text";
import { useRouter } from "@tanstack/react-router";
import {
  ArchiveRestore,
  ArrowRightToLine,
  Ban,
  Calculator,
  Check,
  CheckCheck,
  CheckCircle2,
  CircleCheck,
  ClipboardCheck,
  FileText,
  Flame,
  Inbox,
  Loader2,
  Lock,
  Pause,
  Play,
  Reply,
  Rocket,
  RotateCcw,
  RotateCw,
  Send,
  ShieldCheck,
  SkipForward,
  Undo2,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTeamPrompt } from "./slot-picker";

/**
 * Compact row-level workflow action buttons for the work-items
 * list. Port of the Next `work-items/row-actions.tsx`. Visibility
 * is pre-resolved server-side (`/api/work-items/list` runs
 * `resolveAvailableActions` per row) so a row only shows buttons
 * the viewer may click.
 *
 * Framework swaps: `next/navigation` useRouter -> TanStack
 * useRouter; the deferred-toast-on-transition dance collapses to
 * a direct toast + `router.invalidate()` because TanStack's
 * invalidate already awaits the refetch.
 */

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  assign_to_dev: UserPlus,
  submit_estimate: Send,
  approve_estimate: Check,
  reject_estimate: Undo2,
  work_done: CheckCircle2,
  no_changes_needed: SkipForward,
  start_task: Play,
  complete_task: ClipboardCheck,
  start_incident: Flame,
  resolve_hotfix: Rocket,
  block: Lock,
  unblock: Ban,
  wait_on_client: Pause,
  return_to_waiting: Pause,
  client_replied: Reply,
  reopen: RotateCw,
  restore: ArchiveRestore,
  close_as_resolved: CircleCheck,
  back_to_backlog: Inbox,
  re_estimate: Calculator,
  back_to_spec: FileText,
  done_without_review: CheckCheck,
};

function iconFor(actionId: string) {
  // Testing-chain pass/return actions share one icon per direction.
  if (actionId.startsWith("advance_from_")) return ShieldCheck;
  if (actionId.startsWith("return_")) return RotateCcw;
  if (actionId.startsWith("skip_")) return SkipForward;
  return ACTION_ICONS[actionId] ?? ArrowRightToLine;
}

export type WorkflowAction = {
  id: string;
  label: string;
  variant: "default" | "outline" | "destructive";
  description?: string;
  requiredFields: string[];
  optionalFields?: string[];
};

type ActionResult = {
  toStatus?: string;
};

export function WorkItemRowActions({
  workItemId,
  actions,
  currentUserId = null,
}: {
  workItemId: string;
  actions: WorkflowAction[];
  currentUserId?: string | null;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [modalAction, setModalAction] = useState<WorkflowAction | null>(null);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [comment, setComment] = useState("");
  const [team, setTeam] = useState<Record<string, string>>({});
  const teamPrompt = useTeamPrompt();

  const execute = useRowMutation(
    (actionId: string, payload: Record<string, unknown> | undefined, _label: string | undefined) =>
      api.post<ActionResult>(`/api/work-items/${workItemId}/actions`, { actionId, payload }),
    {
      rowKey: (actionId) => actionId,
      onSuccess: (data, _actionId, _payload, label) => {
        toast({
          title: label ?? t("wiRow.actionCompleted"),
          ...(data.toStatus
            ? { description: t("wiRow.statusArrow", { state: data.toStatus }) }
            : {}),
          variant: "success",
        });
        router.invalidate();
      },
      errorMessage: (err) =>
        err instanceof ApiError && typeof err.detail === "object" && err.detail !== null
          ? ((err.detail as { error?: string; reason?: string }).error ??
            (err.detail as { reason?: string }).reason ??
            err.message)
          : err instanceof Error
            ? err.message
            : t("wiRow.error"),
      onError: (msg, _actionId, _payload, label) =>
        toast({
          title: label ?? t("wiRow.actionFailed"),
          description: msg,
          variant: "destructive",
        }),
    },
  );

  async function handleClick(action: WorkflowAction) {
    const picked = await teamPrompt.ask(workItemId, action.id);
    if (picked === null) return;
    setTeam(picked);
    const withTeam = (p: Record<string, unknown> = {}) =>
      Object.keys(picked).length ? { ...p, team: picked } : Object.keys(p).length ? p : undefined;
    // Fast path: the only required input is `assigneeId` -> self-claim
    // silently. The full member picker lives on the detail page.
    const onlyNeedsAssignee =
      action.requiredFields.length === 1 &&
      action.requiredFields[0] === "assigneeId" &&
      !action.optionalFields?.length;
    if (onlyNeedsAssignee && currentUserId) {
      execute.run(action.id, withTeam({ assigneeId: currentUserId }), workflowText(action.label));
      return;
    }
    const hasFields = action.requiredFields.length > 0 || (action.optionalFields?.length ?? 0) > 0;
    if (hasFields) {
      setModalAction(action);
      setEstimatedHours("");
      setComment("");
    } else {
      execute.run(action.id, withTeam(), workflowText(action.label));
    }
  }

  function submitModal() {
    if (!modalAction) return;
    const payload: Record<string, unknown> = {};
    if (modalAction.requiredFields.includes("estimatedHours")) {
      const h = Number.parseFloat(estimatedHours);
      if (!h || h <= 0) return;
      payload.estimatedHours = h;
    }
    if (
      (modalAction.requiredFields.includes("comment") ||
        modalAction.optionalFields?.includes("comment")) &&
      comment.trim()
    ) {
      payload.comment = comment.trim();
    }
    if (Object.keys(team).length) payload.team = team;
    execute.run(modalAction.id, payload, workflowText(modalAction.label));
    setModalAction(null);
  }

  if (actions.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <>
      {teamPrompt.dialog}
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-wrap items-center gap-1">
          {actions.map((action) => {
            const Icon = iconFor(action.id);
            return (
              <Tooltip key={action.id}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant={action.variant}
                    disabled={execute.busy}
                    onClick={() => handleClick(action)}
                    aria-label={workflowText(action.label)}
                    className="h-7 w-7"
                  >
                    {execute.isRunning(action.id) ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Icon className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="font-medium">{workflowText(action.label)}</p>
                  {action.description && (
                    <p className="text-xs text-muted-foreground max-w-[220px]">
                      {workflowText(action.description)}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      <Dialog open={!!modalAction} onOpenChange={(open) => !open && setModalAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{modalAction ? workflowText(modalAction.label) : null}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {modalAction?.requiredFields.includes("estimatedHours") && (
              <label className="block">
                <span className="text-sm font-medium block mb-1.5">
                  {t("wiRow.estimatedHours")}
                </span>
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value)}
                  placeholder="0.00"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
            )}
            {(modalAction?.requiredFields.includes("comment") ||
              modalAction?.optionalFields?.includes("comment")) && (
              <label className="block">
                <span className="text-sm font-medium block mb-1.5">
                  {t("wiRow.comment")}{" "}
                  {modalAction?.optionalFields?.includes("comment") &&
                    !modalAction?.requiredFields.includes("comment") && (
                      <span className="text-muted-foreground font-normal">
                        {t("wiRow.optional")}
                      </span>
                    )}
                </span>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("wiRow.notePlaceholder")}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setModalAction(null)}>
              {t("wiRow.cancel")}
            </Button>
            <Button size="sm" onClick={submitModal}>
              {modalAction ? workflowText(modalAction.label) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
